// Conversation thread dialog — full two-way SMS history with one number.
// Opened by tapping a phone number in the SMS Inbox.
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { MessageSquare, Loader2, ArrowUpRight, ArrowDownLeft, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SMS_ROLES = new Set(['master_admin', 'admin', 'manager', 'supervisor', 'domain_admin', 'domain_supervisor']);
const MAX_LEN = 459;

const when = (iso) => iso
  ? new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  : '';

export const SmsThreadDialog = ({ phone, onClose }) => {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const canReply = !!user && SMS_ROLES.has(user.role) && !!user.sms_access;

  const load = useCallback(() => {
    axios.get(`${API}/sms/thread`, { params: { phone } })
      .then((r) => setRows(r.data.rows || []))
      .catch(() => setRows([]));
  }, [phone]);

  useEffect(() => {
    if (!phone) return;
    setRows(null);
    setMsg('');
    load();
  }, [phone, load]);

  const sendReply = async () => {
    if (!msg.trim() || sending) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/analytics/sms/send-direct`, {
        to_phone: phone, content: msg.trim(),
      });
      toast.success(r.data?.yabbr?.simulated ? 'SMS simulated (test mode)' : 'Reply sent');
      setMsg('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!phone} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="sms-thread-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-teal-600" /> Conversation with {phone}
          </DialogTitle>
          <DialogDescription>Every SMS sent and received with this number, oldest first.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[420px] overflow-y-auto space-y-2 pr-1" data-testid="sms-thread-list">
          {rows === null ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-teal-500" /></div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8" data-testid="sms-thread-empty">
              No messages with this number in your visible history.
            </p>
          ) : rows.map((m) => (
            <div key={`${m.kind}-${m.id}`}
              className={`flex ${m.kind === 'sent' ? 'justify-end' : 'justify-start'}`}
              data-testid={`sms-thread-msg-${m.id}`}>
              <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${m.kind === 'sent'
                ? 'bg-teal-600 text-white rounded-br-sm'
                : 'bg-slate-100 text-slate-800 rounded-bl-sm'}`}>
                <p className="whitespace-pre-wrap break-words">{m.content}</p>
                <p className={`text-[10px] mt-1 flex items-center gap-1 ${m.kind === 'sent' ? 'text-teal-100/80 justify-end' : 'text-slate-400'}`}>
                  {m.kind === 'sent'
                    ? <><ArrowUpRight className="w-3 h-3" /> {when(m.at)}{m.agent ? ` · ${m.agent}` : ''}{m.delivery_status ? ` · ${m.delivery_status}` : ''}</>
                    : <><ArrowDownLeft className="w-3 h-3" /> {when(m.at)}{m.handled ? ' · handled' : ''}</>}
                </p>
              </div>
            </div>
          ))}
        </div>
        {canReply && (
          <div className="border-t border-slate-100 pt-3 mt-1" data-testid="sms-thread-compose">
            <div className="flex items-end gap-2">
              <textarea value={msg} onChange={(e) => setMsg(e.target.value.slice(0, MAX_LEN))}
                rows={2} placeholder={`Reply to ${phone}…`}
                className="flex-1 text-sm border border-slate-200 rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                data-testid="sms-thread-reply-input" />
              <button onClick={sendReply} disabled={sending || !msg.trim()}
                className="shrink-0 h-9 px-4 rounded-full bg-teal-600 hover:bg-teal-500 disabled:opacity-40 text-white text-sm font-bold flex items-center gap-1.5"
                data-testid="sms-thread-reply-send-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Send
              </button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1 text-right">{msg.length}/{MAX_LEN}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
